/**
 * TableWidget - Display tabular data with sorting using CSS Grid
 */

import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Chip, alpha } from "@mui/material";
import { ArrowUpward as ArrowUpIcon, ArrowDownward as ArrowDownIcon } from "@mui/icons-material";
import KDTypography from "components/KDTypography";
import BaseWidget from "./BaseWidget";
import { useMaterialUIController } from "context";
import { useDashboard } from "../context/DashboardContext";
import { getWidgetById, WIDGET_TYPES } from "../config/widgetRegistry";

const getNestedValue = (obj, path) => {
  if (!obj || !path) return undefined;
  const keys = path.split(".");
  let current = obj;
  for (const key of keys) {
    if (current === null || current === undefined) return undefined;
    current = current[key];
  }
  return current;
};

const formatCell = (value, column) => {
  if (value === null || value === undefined) return "—";

  // Handle Firestore timestamp objects
  if (typeof value === "object" && value._seconds !== undefined && value._nanoseconds !== undefined) {
    value = new Date(value._seconds * 1000 + value._nanoseconds / 1000000).toISOString();
  }

  // Handle non-Date objects
  if (typeof value === "object" && !(value instanceof Date) && !React.isValidElement(value)) {
    if (value.toString && value.toString !== Object.prototype.toString) return value.toString();
    return JSON.stringify(value);
  }

  const columnLower = column.toLowerCase();

  if (columnLower.includes("date") || columnLower.includes("createdat") || columnLower.includes("at")) {
    if (value instanceof Date || !isNaN(Date.parse(value))) {
      const date = new Date(value);
      const now = new Date();
      const diff = now - date;
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      if (days === 0) return "Today";
      if (days === 1) return "Yesterday";
      if (days < 7) return `${days}d ago`;
      return date.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
    }
  }

  if (columnLower.includes("amount") || columnLower.includes("earnings") || columnLower.includes("revenue")) {
    if (typeof value === "number") {
      return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
    }
  }

  if (columnLower.includes("rating") && typeof value === "number") {
    return `${value.toFixed(1)} ★`;
  }

  if (columnLower === "status") {
    const statusColors = {
      active: "#10B981", completed: "#10B981", succeeded: "#10B981", healthy: "#10B981", online: "#10B981", published: "#10B981",
      pending: "#F59E0B", processing: "#F59E0B",
      inactive: "#64748B", draft: "#64748B", archived: "#64748B",
      failed: "#EF4444", error: "#EF4444", offline: "#EF4444", cancelled: "#EF4444", suspended: "#EF4444",
    };

    const color = statusColors[value?.toLowerCase()] || "#64748B";
    return (
      <Chip
        label={value}
        size="small"
        sx={{
          bgcolor: alpha(color, 0.2),
          color: color,
          fontWeight: 600,
          fontSize: "0.65rem",
          height: 20,
          "& .MuiChip-label": { px: 1 },
        }}
      />
    );
  }

  if (typeof value === "number") return value.toLocaleString();
  return value;
};

const formatHeader = (column) => {
  return column
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

function TableWidget({ widgetId, data: propData, columns: propColumns, loading: propLoading, error: propError, maxRows = 10, isExpanded = false }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { metricsData, actions } = useDashboard();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const widget = useMemo(() => getWidgetById(widgetId), [widgetId]);

  const { tableData, columns, loading, error } = useMemo(() => {
    if (!widget) return { tableData: [], columns: [], loading: false, error: null };
    const filteredData = actions.getWidgetData(widgetId);
    const categoryData = metricsData[widget.category];
    const rawData = propData ?? filteredData ?? getNestedValue(categoryData, widget.dataKey) ?? getNestedValue(categoryData?.data, widget.dataKey) ?? [];
    return {
      tableData: Array.isArray(rawData) ? rawData : [],
      columns: propColumns ?? widget.columns ?? [],
      loading: propLoading ?? metricsData[widget.category] === undefined,
      error: propError ?? null,
    };
  }, [widget, metricsData, actions, widgetId, propData, propColumns, propLoading, propError]);

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return tableData;
    return [...tableData].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      }
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      return sortConfig.direction === "asc" ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  }, [tableData, sortConfig]);

  if (!widget) return null;

  const displayData = sortedData.slice(0, maxRows);

  const handleSort = (column) => {
    setSortConfig((prev) => ({
      key: column,
      direction: prev.key === column && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const gridTemplateColumns = columns
    .map((col) => {
      const c = col.toLowerCase();
      if (c === "status") return "minmax(70px, 0.8fr)";
      if (c.includes("date") || c.includes("at")) return "minmax(80px, 1fr)";
      if (c.includes("amount") || c.includes("earnings") || c.includes("revenue")) return "minmax(80px, 1fr)";
      if (c.includes("rating")) return "minmax(60px, 0.8fr)";
      if (c.includes("name") || c.includes("user") || c.includes("email")) return "minmax(100px, 1.5fr)";
      if (c === "title" || c.includes("description")) return "minmax(120px, 2fr)";
      return "minmax(80px, 1.5fr)";
    })
    .join(" ");

  const rowBaseStyle = {
    display: "grid",
    gridTemplateColumns,
    gap: 2,
    alignItems: "center",
    px: 2,
    py: 1,
  };

  return (
    <BaseWidget
      id={widgetId}
      title={widget.title}
      icon={widget.icon}
      color={widget.categoryColor}
      gradient={widget.categoryGradient}
      loading={loading}
      error={error}
      showDragHandle
      noPadding
      isExpanded={isExpanded}
    >
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {displayData.length === 0 ? (
          <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
            <KDTypography variant="caption" sx={{ color: darkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)" }}>No data available</KDTypography>
          </Box>
        ) : (
          <Box>
            {/* Header */}
            <Box sx={{ ...rowBaseStyle, bgcolor: darkMode ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)", borderBottom: darkMode ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.06)" }}>
              {columns.map((column) => (
                <Box
                  key={column}
                  sx={{
                    color: darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    cursor: "pointer",
                    userSelect: "none",
                    "&:hover": { color: darkMode ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.7)" },
                  }}
                  onClick={() => handleSort(column)}
                >
                  <KDTypography
                    variant="caption"
                    sx={{
                      color: sortConfig.key === column ? "#7C3AED" : "inherit",
                      fontWeight: 600,
                      fontSize: "0.7rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {formatHeader(column)}
                  </KDTypography>
                  {sortConfig.key === column &&
                    (sortConfig.direction === "asc" ? (
                      <ArrowUpIcon sx={{ fontSize: 14, color: "#7C3AED" }} />
                    ) : (
                      <ArrowDownIcon sx={{ fontSize: 14, color: "#7C3AED" }} />
                    ))}
                </Box>
              ))}
            </Box>
            {/* Data Rows */}
            {displayData.map((row, index) => (
              <Box
                key={row.id || index}
                sx={{
                  ...rowBaseStyle,
                  borderBottom: darkMode ? "1px solid rgba(255,255,255,0.03)" : "1px solid rgba(0,0,0,0.04)",
                  "&:hover": { bgcolor: darkMode ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)" },
                }}
              >
                {columns.map((column) => (
                  <Box key={column} sx={{ color: darkMode ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.7)", fontSize: "0.75rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {formatCell(row[column], column)}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {tableData.length > maxRows && (
        <Box sx={{ p: 1, borderTop: darkMode ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.06)", textAlign: "center" }}>
          <KDTypography variant="caption" sx={{ color: darkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)" }}>
            Showing {maxRows} of {tableData.length}
          </KDTypography>
        </Box>
      )}
    </BaseWidget>
  );
}

TableWidget.propTypes = {
  widgetId: PropTypes.string.isRequired,
  data: PropTypes.array,
  columns: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.bool,
  error: PropTypes.shape({ message: PropTypes.string }),
  maxRows: PropTypes.number,
  isExpanded: PropTypes.bool,
};

export default TableWidget;
